import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Clock, Star, Users, Award, ArrowRight, CheckCircle, Ticket } from 'lucide-react';
import { COMPANY_CONFIG } from '../config/config';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Safety First',
      description: 'We prioritize the safety of our passengers above everything else. All our buses undergo regular maintenance and safety checks to ensure your peace of mind.'
    },
    {
      icon: Clock,
      title: 'Punctuality',
      description: 'We understand the value of time. Our commitment to punctuality ensures you reach your destination on schedule, every time.'
    },
    {
      icon: Star,
      title: 'Quality Service',
      description: 'We provide premium travel experience with comfortable seating, modern amenities, air conditioning, and professional staff.'
    },
    {
      icon: Users,
      title: 'Customer Focus',
      description: 'Our customers are at the heart of everything we do. We strive to exceed your expectations with personalized service and attention to detail.'
    }
  ];

  const achievements = [
    '24+ Years of Excellence',
    'Modern Fleet of Buses',
    'Multiple Routes Across Sri Lanka',
    'Thousands of Satisfied Customers',
    '24/7 Customer Support',
    'Commitment to Safety & Quality'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-50">
      {/* Header Section */}
      <section className="relative bg-[#00336a] text-white py-8 sm:py-12 lg:py-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 sm:w-80 sm:h-80 bg-blue-900 rounded-full opacity-20 blur-2xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 sm:w-80 sm:h-80 bg-blue-900 rounded-full opacity-20 blur-2xl"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 text-center">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-3 sm:mb-4 drop-shadow-lg">About {COMPANY_CONFIG.appname}</h1>
          <p className="text-sm sm:text-base lg:text-lg text-blue-100 max-w-3xl mx-auto">
            Your Trusted Partner in Transportation for Over Two Decades
          </p>
        </div>
      </section>

      {/* Hero Story Section with Image */}
      <section className="py-8 sm:py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="rounded-xl overflow-hidden shadow-lg transform hover:scale-[1.02] transition-transform duration-300">
                <img 
                  src="/images/1.jpeg" 
                  alt={`${COMPANY_CONFIG.appname} - Our Journey`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-3 -right-3 bg-[#00336a] text-white p-3 sm:p-4 rounded-lg shadow-lg hidden lg:block">
                <div className="text-2xl sm:text-3xl font-extrabold mb-0.5">24+</div>
                <div className="text-xs sm:text-sm font-semibold">Years of Service</div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="inline-block bg-blue-100 text-blue-800 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold mb-3">
                Our Journey
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-3 sm:mb-4 leading-tight">
                Excellence Since 2000
              </h2>
              <div className="space-y-3 sm:space-y-4 text-sm sm:text-base text-gray-600 leading-relaxed">
                <p className="text-base sm:text-lg">
                  Founded in 2000, {COMPANY_CONFIG.appname} has been at the forefront of Sri Lanka's 
                  transportation industry, providing safe, reliable, and comfortable bus services 
                  to passengers across the country.
                </p>
                <p>
                  What started as a small family business has grown into one of the most trusted 
                  names in long-distance travel. We connect people from Colombo to various destinations 
                  across Sri Lanka with dedication, excellence, and a commitment to customer satisfaction.
                </p>
                <p>
                  Today, we operate a modern fleet of buses, serving carefully selected routes 
                  with the same commitment to quality and safety that defined our journey from day one.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-8 sm:py-12 lg:py-16 bg-gradient-to-b from-blue-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-2 sm:mb-3">
              Our Core Values
            </h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do and define who we are
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div 
                  key={index} 
                  className="group bg-white rounded-xl p-4 sm:p-6 lg:p-8 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-transparent hover:border-blue-200"
                >
                  <div className="flex flex-col lg:flex-row items-start lg:items-center space-y-3 lg:space-y-0 lg:space-x-4">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#00336a] rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-105 transition-transform duration-300">
                      <Icon className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg sm:text-xl font-extrabold text-gray-900 mb-2">
                        {value.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-8 sm:py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10">
            {/* Mission */}
            <div className="bg-[#00336a] text-white rounded-xl p-5 sm:p-6 lg:p-8 shadow-lg transform hover:scale-[1.02] transition-transform duration-300">
              <div className="flex items-center mb-4 sm:mb-5">
                <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl flex items-center justify-center mr-3 sm:mr-4">
                  <Award className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-extrabold">Our Mission</h3>
              </div>
              <p className="text-sm sm:text-base lg:text-lg leading-relaxed text-blue-50">
                To provide safe, reliable, and comfortable transportation services that connect 
                people across Sri Lanka, while maintaining the highest standards of customer 
                service and operational excellence. We are committed to making travel accessible, 
                affordable, and enjoyable for everyone.
              </p>
            </div>
            
            {/* Vision */}
            <div className="bg-[#00336a] text-white rounded-xl p-5 sm:p-6 lg:p-8 shadow-lg transform hover:scale-[1.02] transition-transform duration-300">
              <div className="flex items-center mb-4 sm:mb-5">
                <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl flex items-center justify-center mr-3 sm:mr-4">
                  <Star className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-extrabold">Our Vision</h3>
              </div>
              <p className="text-sm sm:text-base lg:text-lg leading-relaxed text-blue-50">
                To be the leading transportation company in Sri Lanka, recognized for innovation, 
                sustainability, and exceptional customer experience. We aim to set new industry 
                standards for safety, service quality, and passenger satisfaction while expanding 
                our network to serve more communities across the country.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements & Features Section */}
      <section className="py-8 sm:py-12 lg:py-16 bg-gradient-to-b from-blue-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 items-center">
            <div>
              <div className="inline-block bg-blue-100 text-blue-800 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold mb-3">
                Our Commitment
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-3 sm:mb-4">
                Why Choose Us
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                At {COMPANY_CONFIG.appname}, we are committed to providing the best possible travel 
                experience. Our dedication to excellence is reflected in every aspect of our service.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {achievements.map((achievement, index) => (
                  <div key={index} className="flex items-center space-x-2 bg-white rounded-lg p-2.5 sm:p-3 shadow-sm hover:shadow-md transition-shadow">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#00336a] flex-shrink-0" />
                    <span className="text-gray-700 font-medium text-xs sm:text-sm">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-xl overflow-hidden shadow-lg transform hover:scale-[1.02] transition-transform duration-300">
                <img 
                  src="/images/2.jpeg" 
                  alt={`${COMPANY_CONFIG.appname} - Quality Fleet`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 sm:py-12 lg:py-16 bg-[#00336a] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-blue-900/50"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-3 sm:mb-4">
            Ready to Travel with Us?
          </h2>
          <p className="text-sm sm:text-base lg:text-lg mb-6 sm:mb-8 text-blue-100 max-w-3xl mx-auto">
            Experience the {COMPANY_CONFIG.appname} difference and enjoy comfortable, safe, and reliable transportation
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://www.tripimate.com"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#00336a] px-5 sm:px-6 lg:px-8 py-2 sm:py-2.5 lg:py-3 rounded-lg text-sm sm:text-base font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center justify-center"
            >
              <span>Book Online Now</span>
            </a>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-5 sm:px-6 lg:px-8 py-2 sm:py-2.5 lg:py-3 rounded-lg text-sm sm:text-base font-bold hover:bg-white hover:text-[#00336a] transition-all duration-300 inline-flex items-center justify-center"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
